import React, { useEffect, useState } from "react";
import {
  ArrowLeft,
  Loader,
  CheckCircle,
  WashingMachine,
  HandCoins,
  AlertCircle,
  MessageCircle,
  History
} from "lucide-react";
import { Link, useLocation } from "react-router-dom";

export default function TrackOrder() {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const orderIdFromURL = queryParams.get("id");
  const student = JSON.parse(localStorage.getItem("student"));

  const [orderId, setOrderId] = useState(orderIdFromURL || null);
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  const isDemo = localStorage.getItem("demoMode") === "true";

  const fetchOrder = async () => {
    try {
      if (!orderId) return;
     const res = await fetch(`https://hostel-laundry-api.onrender.com/orders/get/${orderId}`);

      const data = await res.json();
      if (res.ok) setOrder(data);
      setLoading(false);
    } catch (error) {
      console.error(error);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isDemo) {
      // Demo mode: show dummy order
      setOrderId(orderIdFromURL || "demo-1042");
      setOrder({
        id: orderIdFromURL || "demo-1042",
        usn: "demo2025",
        status: "washing",
        shirts: 3,
        pants: 2,
        uniforms: 1,
        towels: 0,
        bedsheets: 1,
        created_at: new Date().toISOString()
      });
      setLoading(false);
      return;
    }

    if (!orderIdFromURL && student) {
      fetch(`https://hostel-laundry-api.onrender.com/orders/latest/${student.usn}`)

        .then((res) => res.json())
        .then((data) => setOrderId(data.orderId || null));
    }
  }, []);

  useEffect(() => {
    if (isDemo) return; // skip polling in demo
    if (!orderId) return;
    fetchOrder();
    const interval = setInterval(fetchOrder, 10000);
    return () => clearInterval(interval);
  }, [orderId]);

  if (loading || !order) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <Loader className="animate-spin w-8 h-8 text-neutral-900" />
      </div>
    );
  }

  const statusTextMap = {
    received: "Received",
    washing: "Washing",
    drying: "Drying",
    ready: "Ready",
    delivered: "Delivered",
  };

  const currentState = statusTextMap[order.status] || "In Progress";

  const steps = [
    { id: "picked", label: "Picked Up", icon: HandCoins },
    { id: "washing", label: "Washing / Drying", icon: WashingMachine },
    { id: "delivered", label: "Delivered", icon: CheckCircle },
  ];

  const getStepIndexFromStatus = (status) => {
    switch (status) {
      case "received":
        return 0;
      case "washing":
      case "drying":
      case "ready":
        return 1;
      case "delivered":
        return 2;
      default:
        return 0;
    }
  };

  const currentStepIndex = getStepIndexFromStatus(order.status);
  const progressRatio =
    steps.length > 1 ? currentStepIndex / (steps.length - 1) : 0;

  const nodePositions = [
    { left: "12%", top: "25%" },
    { left: "50%", top: "70%" },
    { left: "88%", top: "25%" },
  ];

  const handleReport = () => {
    window.location.href = `/report?order=${orderId}`;
  };

  const handleFeedback = () => {
    window.location.href = `/feedback?order=${orderId}`;
  };

  return (
    <div className="min-h-screen bg-white px-4 py-5 pb-24 flex justify-center">
      <div className="w-full max-w-3xl space-y-5">

        {/* HEADER */}
        <header className="flex items-center justify-between bg-white/80 backdrop-blur-lg border border-neutral-200 rounded-xl px-4 py-3">

          {/* Back */}
          <Link
            to="/student-dashboard"
            className="p-2 rounded-full hover:bg-neutral-100 transition"
          >
            <ArrowLeft className="w-5 h-5 text-neutral-700" />
          </Link>

          {/* Status */}
          <div className="flex flex-col items-center">
            <span className="text-[10px] uppercase tracking-[0.16em] text-neutral-400 font-medium">
              Status
            </span>
            <span className="text-base font-semibold text-neutral-900">
              {currentState}
            </span>
          </div>

          {/* HISTORY BUTTON */}
          <button
            onClick={() => (window.location.href = "/history")}
            className="p-2 rounded-full hover:bg-neutral-100 border border-neutral-200 transition flex items-center justify-center"
          >
            <History className="w-5 h-5 text-neutral-700" />
          </button>

        </header>

        {/* TRACKER CARD */}
        <main className="bg-white rounded-2xl border border-neutral-200 px-6 py-6 md:px-8 md:py-8">
          <div className="mb-6">
            <h1 className="text-2xl md:text-3xl font-semibold text-neutral-900">
              Track Your Order
            </h1>
            <p className="text-sm text-neutral-500 mt-1">
              Order ID: #{orderId}
            </p>
          </div>

          {/* S CURVED TRACKER */}
          <div className="relative w-full h-56 md:h-64">
            <svg
              viewBox="0 0 100 100"
              className="absolute inset-0 w-full h-full"
              preserveAspectRatio="none"
            >
              <path
                d="M 12 25 C 35 25, 35 75, 50 75 C 65 75, 65 25, 88 25"
                fill="none"
                stroke="#e5e5e5"
                strokeWidth="2.5"
                strokeLinecap="round"
              />
              <path
                d="M 12 25 C 35 25, 35 75, 50 75 C 65 75, 65 25, 88 25"
                fill="none"
                stroke="#171717"
                strokeWidth="2.5"
                strokeLinecap="round"
                pathLength="100"
                strokeDasharray="100"
                strokeDashoffset={100 - progressRatio * 100}
              />
            </svg>

            {steps.map((step, index) => {
              const Icon = step.icon;
              const completed = index <= currentStepIndex;
              const pos = nodePositions[index];

              return (
                <div
                  key={step.id}
                  className="absolute flex flex-col items-center"
                  style={{
                    left: pos.left,
                    top: pos.top,
                    transform: "translate(-50%, -50%)",
                  }}
                >
                  <div
                    className={`w-11 h-11 flex items-center justify-center rounded-full transition-all duration-300 ${
                      completed
                        ? "bg-neutral-900 text-white"
                        : "bg-neutral-100 text-neutral-400 border border-neutral-200"
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                  </div>
                  <span
                    className={`mt-2 text-xs font-medium ${
                      completed ? "text-neutral-900" : "text-neutral-400"
                    }`}
                  >
                    {step.label}
                  </span>
                </div>
              );
            })}
          </div>
        </main>

        {/* REPORT + FEEDBACK */}
        <div className="flex items-center justify-center gap-3 pt-1">
          <button
            onClick={handleReport}
            className="flex-1 py-3 bg-white border border-neutral-200 text-neutral-700 rounded-xl font-medium text-sm hover:bg-neutral-50 hover:border-neutral-300 transition flex items-center justify-center gap-2"
          >
            <AlertCircle className="w-4 h-4" /> Report
          </button>

          <button
            onClick={handleFeedback}
            className="flex-1 py-3 bg-neutral-900 text-white rounded-xl font-medium text-sm hover:bg-neutral-800 transition flex items-center justify-center gap-2 shadow-sm"
          >
            <MessageCircle className="w-4 h-4" /> Feedback
          </button>
        </div>

      </div>
    </div>
  );
}
